import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { WidgetField } from "@/domains/buildings/types/buildingWidget";
import type { AttributeHistoryEntry } from "@/hooks/buildings/useAttributeHistory";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { ActorSummary, EditAvatar } from "./HistoryTimelineItem";
import type { HistoryRelativeTimeCopy } from "./historyActor";

interface HistoryEntryProps {
  entry: AttributeHistoryEntry;
  field: WidgetField;
  language: string;
  tw: BuildingDataWidgetSections;
  relativeTimeCopy: HistoryRelativeTimeCopy;
}

function InspectorHistoryEntry(props: HistoryEntryProps) {
  return (
    <li className="flex gap-3">
      <EditAvatar entry={props.entry} unknownUser={props.tw.unknownUser} />
      <div className="min-w-0 flex-1">
        <ActorSummary
          entry={props.entry}
          language={props.language}
          unknownUser={props.tw.unknownUser}
          relativeTimeCopy={props.relativeTimeCopy}
        />
        <div className="mt-1 break-words text-sm text-foreground">
          {props.entry.attributeValue || props.tw.statusMissing}
          {props.field.unit && props.entry.attributeValue ? (
            <span className="ml-1 text-xs text-muted-foreground">{props.field.unit}</span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function InspectorHistoryEntries(props: {
  entries: AttributeHistoryEntry[];
  field: WidgetField;
  language: string;
  tw: BuildingDataWidgetSections;
  relativeTimeCopy: HistoryRelativeTimeCopy;
}) {
  return (
    <ol className="mt-3 space-y-4">
      {props.entries.slice(0, 8).map((entry, index) => (
        <InspectorHistoryEntry
          key={`${entry.createdAt}-${index}`}
          entry={entry}
          field={props.field}
          language={props.language}
          tw={props.tw}
          relativeTimeCopy={props.relativeTimeCopy}
        />
      ))}
    </ol>
  );
}

interface BuildingDataInspectorHistoryProps {
  entries: AttributeHistoryEntry[] | undefined;
  field: WidgetField;
  language: string;
  isLoading: boolean;
  isError: boolean;
  tw: BuildingDataWidgetSections;
  onRetry: () => void;
}

/**
 * A failed history query must never render as "no history" — an attribute with
 * edits would silently read as one that has none.
 */
function HistoryBody(
  props: BuildingDataInspectorHistoryProps & { relativeTimeCopy: HistoryRelativeTimeCopy }
) {
  const { entries, field, language, isLoading, isError, tw, relativeTimeCopy, onRetry } = props;
  if (isLoading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 size={13} className="animate-spin" />
        {tw.loadingHistory}
      </div>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="mt-3 flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2"
      >
        <span className="text-xs text-destructive">{tw.historyLoadError}</span>
        <Button variant="outline" size="sm" className="h-7 shrink-0" onClick={onRetry}>
          {tw.retryHistory}
        </Button>
      </div>
    );
  }
  if (!entries?.length) return <p className="mt-3 text-xs text-muted-foreground">{tw.noHistory}</p>;
  return (
    <InspectorHistoryEntries
      entries={entries}
      field={field}
      language={language}
      tw={tw}
      relativeTimeCopy={relativeTimeCopy}
    />
  );
}

export function BuildingDataInspectorHistory(props: BuildingDataInspectorHistoryProps) {
  const { t } = useLanguage();
  return (
    <section className="py-4">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {props.tw.history}
      </h3>
      <HistoryBody {...props} relativeTimeCopy={t.documents.log.history} />
    </section>
  );
}
